// §34.3 shop bays + availability-first job scheduling - client data hooks.
import { keepPreviousData, useMutation, useQueries, useQuery, useQueryClient } from '@tanstack/react-query';
import type {
  Bay, CreateBayInput, DayAvailabilityDto, DayDetail, ScheduleJobInput, ScheduleJobResult, UpdateBayInput,
} from '@wrapforce/shared';
import { workspacePath } from '@wrapforce/shared/api-client';
import { api } from '@/lib/api';

export function useBays(workspaceId: string | null) {
  return useQuery({
    queryKey: [workspaceId, 'bays'],
    queryFn: () => api.get<Bay[]>(workspacePath(workspaceId!, '/bays')),
    enabled: !!workspaceId,
  });
}

function useInvalidateBays(workspaceId: string | null) {
  const queryClient = useQueryClient();
  return () => {
    queryClient.invalidateQueries({ queryKey: [workspaceId, 'bays'] });
    queryClient.invalidateQueries({ queryKey: [workspaceId, 'scheduling-availability'] });
  };
}

export function useCreateBay(workspaceId: string | null) {
  const invalidate = useInvalidateBays(workspaceId);
  return useMutation({
    mutationFn: (input: CreateBayInput) => api.post<Bay>(workspacePath(workspaceId!, '/bays'), input),
    onSuccess: invalidate,
  });
}

export function useUpdateBay(workspaceId: string | null) {
  const invalidate = useInvalidateBays(workspaceId);
  return useMutation({
    mutationFn: ({ bayId, input }: { bayId: string; input: UpdateBayInput }) =>
      api.patch(workspacePath(workspaceId!, `/bays/${bayId}`), input),
    onSuccess: invalidate,
  });
}

export function useAvailability(workspaceId: string | null, from: string, to: string, locationId?: string) {
  return useQuery({
    queryKey: [workspaceId, 'scheduling-availability', from, to, locationId],
    queryFn: () => api.get<DayAvailabilityDto[]>(workspacePath(workspaceId!, '/scheduling/availability'), { query: { from, to, locationId } }),
    enabled: !!workspaceId,
    placeholderData: keepPreviousData,
  });
}

export function useDayDetail(workspaceId: string | null, date: string | null, jobId: string | undefined, locationId?: string) {
  return useQuery({
    queryKey: [workspaceId, 'scheduling-day', date, jobId, locationId],
    queryFn: () => api.get<DayDetail>(workspacePath(workspaceId!, '/scheduling/day'), { query: { date: date!, jobId, locationId } }),
    enabled: !!workspaceId && !!date,
    placeholderData: keepPreviousData,
  });
}

// §34.4 - fetch day detail for a list of dates at once (multi-day job spans),
// sharing the same cache keys as useDayDetail so a single day is never fetched
// twice. Returns react-query results in the same order as `dates`.
export function useDayDetails(workspaceId: string | null, dates: string[], jobId: string | undefined, locationId?: string) {
  return useQueries({
    queries: dates.map((date) => ({
      queryKey: [workspaceId, 'scheduling-day', date, jobId, locationId],
      queryFn: () => api.get<DayDetail>(workspacePath(workspaceId!, '/scheduling/day'), { query: { date, jobId, locationId } }),
      enabled: !!workspaceId,
      placeholderData: keepPreviousData,
    })),
  });
}

export function useScheduleJob(workspaceId: string | null, jobId: string) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (input: ScheduleJobInput) => api.post<ScheduleJobResult>(workspacePath(workspaceId!, `/jobs/${jobId}/schedule`), input),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: [workspaceId, 'jobs'] });
      queryClient.invalidateQueries({ queryKey: [workspaceId, 'scheduling-availability'] });
      queryClient.invalidateQueries({ queryKey: [workspaceId, 'scheduling-day'] });
    },
    onError: () => {
      queryClient.invalidateQueries({ queryKey: [workspaceId, 'scheduling-day'] });
    },
  });
}
